import { connectedDestination } from '@/lib/planning/connectedDestination'
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, Check } from 'lucide-react'
import { readSeasons } from '@/lib/cadence/seasons'
import { useReferenceLists } from '@/components/reference/ReferenceListsContext'
import { DomainSwitcher } from '@/components/domain/DomainSwitcher'
import { HeaderPeopleFilter } from './HeaderPeopleFilter'
import { DesktopCenterContext } from '@/components/layout/DesktopNavigation'
import { railEntries } from '@/lib/planning/horizonNumerals'
import { readCadenceConfig } from '@/lib/cadence/config'

/** Phone: a page's own header controls (filters, ⋯) join the horizon-tab row
 *  instead of adding rows above the date. Falls back to inline rendering. */
export const MobilePlanControlsContext = createContext<HTMLElement | null>(null)
export function MobilePlanControls({ children }: { children: ReactNode }) {
  const host = useContext(MobilePlanControlsContext)
  return host ? createPortal(children, host) : <>{children}</>
}

const PERIODS = ['today', 'week', 'month', 'season', 'year'] as const
export function planPeriodForPath(path: string) {
  if (path === '/' || path.startsWith('/tasks-new')) return 'today'
  return PERIODS.find(period => path === `/${period}` || path.startsWith(`/${period}/`))
}

/** The horizon on screen. A Year, Season or Month page names its horizon in
 *  the URL (`horizon`), which wins over the path (Season shown from /year). */
export function shownPeriodFor(path: string, search: string) {
  const period = planPeriodForPath(path)
  if (period !== 'month' && period !== 'season' && period !== 'year') return period
  const h = new URLSearchParams(search).get('horizon')
  return h && /^[0-2]$/.test(h) ? (['year', 'season', 'month'] as const)[Number(h)] : period
}

/**
 * Where the `Planner` entry in the primary navigation points.
 *
 * On a planner page it points at the page you are on, so the entry reads as
 * "you are here" rather than sending you somewhere else. From anywhere else —
 * Routines, Inbox, a detail route — it points at Today.
 *
 * It used to fall back to the last horizon you visited, remembered in
 * localStorage. That made Today cost two clicks for the rest of the session
 * once you had opened Month or Season (walk finding S1-11: "it should be VERY
 * easy to get back tro the today page"). Today is the page the whole app exists
 * to make right; it is never more than one click away now, and the horizon tabs
 * sit on the page for everything else.
 */
export function usePlanDestination() {
  const { pathname, search } = useLocation()
  return connectedDestination(`/${planPeriodForPath(pathname) ?? 'today'}`, search)
}

const HORIZON_NAMES: Record<typeof PERIODS[number], string> = {
  today: 'Today', week: 'Week', month: 'Month', season: 'Season', year: 'Year',
}

function horizonSubtitle(period: typeof PERIODS[number], now: Date): string {
  if (period === 'today') return now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  if (period === 'week') return "This week's list and days"
  if (period === 'month') return "This month's goals"
  if (period === 'season') return "This season's goals"
  if (period === 'year') return "This year's goals"
  return ''
}

/** Phone: one horizon at a time, switched from a compact title menu rather
 *  than a row of five tabs (native PlannerView; mockup review 2026-09-23). */
function HorizonSwitcher({ period }: { period: typeof PERIODS[number] }) {
  const {search} = useLocation()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const now = new Date()
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])
  return <div className="horizon-switcher">
    <button type="button" className="horizon-switcher-title" aria-haspopup="menu" aria-expanded={open}
      aria-label={`${HORIZON_NAMES[period]}. Switch horizon`} onClick={() => setOpen(o => !o)}>
      {HORIZON_NAMES[period]}<ChevronDown aria-hidden="true" />
    </button>
    {open && <>
      <button type="button" className="horizon-switcher-scrim" aria-label="Close" onClick={() => setOpen(false)} />
      <div role="menu" aria-label="Planning horizon" className="horizon-switcher-menu">
        {RAIL_ORDER.map(value => <button key={value} type="button" role="menuitemradio" aria-checked={period === value}
          onClick={() => { setOpen(false); if (period === value) return; navigate(connectedDestination(`/${value}`, search)) }}>
          <span><strong>{HORIZON_NAMES[value]}</strong><small>{horizonSubtitle(value, now)}</small></span>
          {period === value && <Check aria-hidden="true" />}
        </button>)}
      </div>
    </>}
  </div>
}

// One line of horizons on every page, small to large (Scott, 2026-10-13: the
// Plan entry and the page's own Year · Season · Month tabs merged into it).
const RAIL_ORDER = ['today', 'week', 'month', 'season', 'year'] as const

/** The period a rail step opens, in words: "Week 41", "October", "Fall 2026". */
function railTitle(value: typeof RAIL_ORDER[number], step: { n: string; label: string }): string {
  if (value === 'today') return 'Today'
  if (value === 'week') return `Week ${step.n}`
  return step.label
}

/** The link for the horizon on screen is "you are here": its own period,
 *  without a narrowed focus. */
function here(pathname: string, search: string): string {
  const params = new URLSearchParams(search)
  params.delete('focus')
  const query = params.toString()
  return connectedDestination(query ? `${pathname}?${query}` : pathname, search)
}

function HorizonRail({ period }: { period?: typeof PERIODS[number] }) {
  const { pathname, search } = useLocation()
  // The rail wears the period being SHOWN, big to small — October's page
  // says "10 October" and its week, not the clock's September. Today is
  // always today.
  const params = new URLSearchParams(search)
  const start = params.get('start') ?? (period === 'week' ? params.get('date') : null)
  const valid = !!start && /^\d{4}-\d{2}-\d{2}$/.test(start) && (period === 'month' || period === 'season' || period === 'week' || period === 'year')
  const shown = valid ? (() => { const [y, m, d] = start!.split('-').map(Number); return { period: period as 'year' | 'season' | 'month' | 'week', start: new Date(y, m - 1, d) } })() : null
  const steps = railEntries(new Date(), shown, readSeasons(), readCadenceConfig().weekStartsOn)
  return <nav aria-label="Planning period" className="horizon-rail">
    {RAIL_ORDER.map((value, k) => <span key={value} className={`horizon-rail-step${value === 'month' ? ' is-goals-start' : ''}`}>
      {k > 0 && <span className="horizon-rail-join" aria-hidden="true" />}
      {/* A plain Link: the current horizon comes from the page shown, not
          the path (/month can show Year), so NavLink's own matching is off. */}
      <Link to={period === value && value !== 'today' && value !== 'week' ? here(pathname, search) : connectedDestination(steps[value].to, search)}
        aria-current={period === value ? 'page' : undefined} className={period === value ? 'is-current' : undefined}
        aria-label={HORIZON_NAMES[value]} title={railTitle(value, steps[value])}>
        <span className="horizon-rail-l">{HORIZON_NAMES[value]}</span>
      </Link>
    </span>)}
  </nav>
}

/** Page tools are deliberately outside primary destination navigation. */
export function PlanNavigation({ mobile = false, paused = false, mobileControlsRef }: {
  mobile?: boolean; paused?: boolean
  /** Phone only: the slot MobilePlanControls portals into. */
  mobileControlsRef?: (node: HTMLDivElement | null) => void
}) {
  const { pathname, search } = useLocation()
  const period = shownPeriodFor(pathname, search)
  const references = useReferenceLists()
  // No ◎ Goals control (Scott, 2026-09-28: "unnecessary") — the Year,
  // Season and Month pages are where goals are read.
  // No Shelves button on any page (Scott, 2026-10-14): Today's own "This
  // week" column and the Week page's No day yet line hold what it opened. A
  // panel already pinned still closes from its own ×.
  // The kept-pins note ("Lists return when you close the side panel") is
  // still said on Today, even though its chooser button lives on the page.
  const pausedNote = !mobile && paused && !!references && references.pins.length > 0
  // The rail pins to the top once the row above scrolls away (2026-09-29):
  // it is the main navigation. A marker just above it says when it is
  // pinned — the hairline appears then, and the footer's scene steps down.
  const center = useContext(DesktopCenterContext)
  const sentinel = useRef<HTMLDivElement>(null)
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const el = sentinel.current
    if (mobile || !el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { threshold: 0 })
    io.observe(el)
    return () => io.disconnect()
  }, [mobile])
  useEffect(() => {
    document.documentElement.classList.toggle('rail-stuck', stuck)
    return () => document.documentElement.classList.remove('rail-stuck')
  }, [stuck])
  if (!mobile && center) {
    // Desktop in the Shell: the rail sits in the centre of the top bar — one
    // row, ☰ · rail · you (Scott, 2026-09-30); the bar pins as a whole. What
    // this row still carries (Shelves off the planner, the paused note)
    // keeps a slim row of its own, only when there is some.
    return <><div ref={sentinel} className="plan-rail-sentinel" aria-hidden="true" />
      {createPortal(<HorizonRail period={period} />, center)}
      {pausedNote && references && <div className="plan-page-tools is-rail-extras">
        <div className="task-chooser-control">
          <span>Lists return when you close the side panel.</span>
        </div>
      </div>}
    </>
  }
  if (!mobile) {
    // Desktop: the horizon rail IS the main navigation (Scott, 2026-09-28 —
    // "Planner" is gone from the row above). Centred on every page; a page
    // that is no horizon simply has none marked.
    return <><div ref={sentinel} className="plan-rail-sentinel" aria-hidden="true" />
    <div className={`plan-page-tools is-rail${stuck ? ' is-stuck' : ''}`} data-period={period}>
      <div className="plan-rail-side" />
      <HorizonRail period={period} />
      <div className="plan-rail-side is-right">
        {/* No week-range picker here (Scott, 2026-09-28: "I don't understand
            what it is") — the Week heading's own menu offers the same ranges. */}
        {pausedNote && references && <div className="task-chooser-control">
          <span>Lists return when you close the side panel.</span>
        </div>}
      </div>
    </div></>
  }
  if (!period && !pausedNote) return null
  return <div className="plan-page-tools" data-period={period}>
    {period && <div className="plan-period-controls">
      <HorizonSwitcher period={period} />
    </div>}
    {pausedNote && references && <div className="task-chooser-control">
      <span>Lists return when you close the side panel.</span>
    </div>}
    {/* Phone: the life-area lens rides on this row (Today folds it into
        its Filters control instead). */}
    {period && mobile && period !== 'today' && <><HeaderPeopleFilter /><DomainSwitcher /></>}
    {period && mobile && <div ref={mobileControlsRef} className="plan-mobile-controls" />}
  </div>
}
